/**
 * Converts a PascalCase token name to kebab-case.
 * Handles: PrimaryDefault → primary-default, Grey3 → grey-3, ShiftInProgress → shift-in-progress
 */
const toKebabCase = (str) => str
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .replace(/([a-zA-Z])(\d)/g, '$1-$2')
    .toLowerCase();
/** Raw border radius values extracted from the css`` template literals in BKJThemeBorders. */
export const RAW_BORDER_VALUES = {
    DefaultBorder: '8px',
    RoundBorder: '99px',
    BoxBorder: '2px',
};
/** Raw shadow values extracted from the css`` template literals in BKJThemeShadows. */
export const RAW_SHADOW_VALUES = {
    Default: '0px 2px 8px rgba(13, 51, 32, 0.3)',
    DropdownBase: '0px 2px 8px 0px #0d33204d',
    InnerShadow: 'inset 0px -2px 4px rgba(0, 0, 0, 0.05)',
    TableShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
};
/** Raw hex color values from BKJThemeColor, used for :root CSS variable declarations. */
export const RAW_COLOR_VALUES = {
    TextDarkGrey: '#45424E',
    TextSubtleGrey: '#6D7682',
    TextDisabled: '#8B96A3',
    TextWhite: '#FFFFFF',
    TextHighlight1: '#1C0F51',
    TextHighlight2: '#4E4785',
    ErrorClick: '#B93751',
    ErrorDefault: '#AC1936',
    ErrorHover: '#991630',
    SuccessClick: '#008A7A',
    SuccessDefault: '#006F62',
    SuccessHover: '#005C52',
    PrimaryClick: '#6864A0',
    PrimaryDefault: '#4E4785',
    PrimaryHover: '#342A6A',
    SecondaryPendingDefault: '#F5C9A0',
    SecondaryPendingHover: '#F2B57D',
    SecondaryPendingDraftHover: '#FDF5EE',
    SecondaryAcceptedDefault: '#73DDC0',
    SecondaryAcceptedHover: '#40D0A9',
    SecondaryAcceptedDraftHover: '#EBFAF6',
    OtherDefault: '#BFBFDC',
    OtherHover: '#B5B5D1',
    OtherDraftHover: '#F2F2F8',
    WarningBackground: '#FAE1CB',
    ErrorBackground: '#FFE9ED',
    NewlyEdited: '#FFF1C0',
    GreyDivider: '#c5cad1',
    Grey1: '#F7F9FC',
    Grey2: '#EDF1F5',
    Grey3: '#DAE1E9',
    Grey4: '#AEB8C5',
    Grey5: '#8B96A3',
    Grey6: '#6D7682',
    Grey7: '#45424E',
    White: '#FFFFFF',
    AccentGreen: '#00C18C',
    AccentBrand: '#8080BA',
    AccentPurple: '#1C0F51',
    Transparent: 'transparent',
    ShiftInProgress: '#ADEFF3',
    ShiftAccepted: '#73DDC0',
    ShiftAssigned: '#73DDC0',
    ShiftCancelled: '#DAE1E9',
    ShiftCompleted: '#707985',
    ShiftOpen: '#F5C9A0',
    ShiftPending: '#F5C9A0',
    ShiftRejected: '#45424E',
    ShiftRequested: '#F5C9A0',
    ShiftUnfulfilled: '#45424E',
    ShiftTimeOff: '#F7F9FC',
    ShiftOtherPosition: '#bfbfdc',
    ShiftUnfulfilledStroke: '#961E36',
    ShiftSwapped: '#3F6FA8',
    ShiftAbsent: '#FFCADA',
    ShiftPendingBlock: '#BDDDFF',
};
/** Raw pixel spacing values from BKJThemeSpacings, used for :root CSS variable declarations. */
export const RAW_SPACING_VALUES = {
    Spacing4: '4px',
    Spacing8: '8px',
    Spacing16: '16px',
    Spacing24: '24px',
    Spacing32: '32px',
    Spacing40: '40px',
};
const buildColorVarName = (key) => `--bkj-color-${toKebabCase(key)}`;
const buildSpacingVarName = (key) => {
    const num = key.replace(/\D/g, '');
    return `--bkj-spacing-${num}`;
};
const buildBorderVarName = (key) => {
    const name = key.replace(/Border$/, '');
    return `--bkj-border-${toKebabCase(name)}`;
};
const buildShadowVarName = (key) => `--bkj-shadow-${toKebabCase(key)}`;
/** Token-to-CSS-variable-name mapping, organized by category. */
export const CSS_VAR_MAP = {
    colors: Object.fromEntries(Object.keys(RAW_COLOR_VALUES).map((key) => [
        key,
        buildColorVarName(key),
    ])),
    spacing: Object.fromEntries(Object.keys(RAW_SPACING_VALUES).map((key) => [
        key,
        buildSpacingVarName(key),
    ])),
    borders: Object.fromEntries(Object.keys(RAW_BORDER_VALUES).map((key) => [
        key,
        buildBorderVarName(key),
    ])),
    shadows: Object.fromEntries(Object.keys(RAW_SHADOW_VALUES).map((key) => [
        key,
        buildShadowVarName(key),
    ])),
};
/** Generates a CSS string containing all --bkj-* custom property declarations for :root injection. */
export const generateCSSVariableStyles = () => {
    const lines = [];
    for (const [key, varName] of Object.entries(CSS_VAR_MAP.colors)) {
        lines.push(`${varName}: ${RAW_COLOR_VALUES[key]};`);
    }
    for (const [key, varName] of Object.entries(CSS_VAR_MAP.spacing)) {
        lines.push(`${varName}: ${RAW_SPACING_VALUES[key]};`);
    }
    for (const [key, varName] of Object.entries(CSS_VAR_MAP.borders)) {
        lines.push(`${varName}: ${RAW_BORDER_VALUES[key]};`);
    }
    for (const [key, varName] of Object.entries(CSS_VAR_MAP.shadows)) {
        lines.push(`${varName}: ${RAW_SHADOW_VALUES[key]};`);
    }
    return `:root {\n  ${lines.join('\n  ')}\n}`;
};
//# sourceMappingURL=BKJThemeProvider.cssVars.js.map